/* eslint-disable max-lines */
/* eslint-disable complexity */
import { styled } from '@linaria/react';
import { PageProps, graphql } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import React from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Layout from 'src/components/Layout';
import Seo from 'src/components/Seo';
import BazaarvoiceDCC from 'src/components/bazaarvoice/BazaarvoiceDCC';
import ProductCarousel from 'src/components/product/ProductCarousel';
import { getProductStatus } from 'src/components/product/helpers';
import { defaultColors } from 'src/theme/colors';
import { theme } from 'src/theme/theme';
import { getBrand, getProductVariationsList } from 'src/utils/helpers';
import { getHelmetPropsFromMetatagProps, getJsonFromMetatagProps } from 'src/utils/metatagHelpers';
import ProductTabs from './components/ProductTabs';
import ProductTitle from './components/ProductTitle';
import RelatedProducts from './components/RelatedProducts';

const StyledProductContainer = styled(Container)`
  @media (min-width: ${theme.media.lg}) {
    background: linear-gradient(
      to right,
      #ecf0fa 0%,
      #ecf0fa 50%,
      #ecf0fa 50%,
      #ffffff 50%,
      #ffffff 100%
    );
    border-bottom: 1px solid ${defaultColors.border};
    border-top: 1px solid ${defaultColors.border};
  }

  img {
    cursor: pointer;
  }
`;

interface Image {
  gatsbyImage: IGatsbyImageData;
}

interface NodeProps extends NodeContent {
  feeding_instructions: {
    processed: string;
  };
  created: Date;
  highlights: [string];
  field_show_pricespider_retailers?: boolean;
  image: ImageMetadata;
  image_gallery: ImageMetadata[];
  relationships: {
    status: TaxonomyTermStatus;
    brand: TaxonomyTerm;
    skus: ParagraphProductVariation[];
    category: [{ name: string; relationship: any }];
    image: Image;
    image_gallery: Image[];
    thumb_image: Image;
    thumb_image_gallery: Image[];
    label_deck?: {
      publicUrl: string;
    };
    ingredients: TaxonomyTermIngredient[];
    servingSize?: ParagraphServingSize;
  };
  field_bazaarvoice_id: string;
  jwt_token: string;
  statusLink: LinkProps;
}

interface DataProps {
  node: NodeProps;
  relatedProducts: {
    edges: [{ node: NodeProps }];
  };
  translations: Translations;
  feedingCalculator: IStorageFeedingCalculator;
}

const ProductTemplate: React.FC<PageProps<DataProps>> = function ({
  data: { node, relatedProducts, feedingCalculator, translations },
  location,
}) {
  const helmetProps = getHelmetPropsFromMetatagProps(node.metatag);
  const schemaMarkup = getJsonFromMetatagProps(node.metatag);

  const brand = node.relationships?.brand ? getBrand(node.relationships.brand) : null;

  const productStatus = node.relationships.status
    ? getProductStatus(node.relationships.status, node.statusLink)
    : null;

  const skus = node.relationships?.skus;
  const sku: string = skus?.length && skus[0]?.upc ? skus[0].upc : '';
  // const sku: string = skus?.length && skus[0]?.upc ? formatSku(skus[0].upc) : '';

  let images = [];
  let thumbs = [];
  let imageMeta = [];

  if (node.relationships?.image) {
    images.push(node.relationships.image);
    imageMeta.push(node.image);
  }
  if (node.relationships?.thumb_image) {
    thumbs.push(node.relationships.thumb_image);
  }
  if (node.relationships?.image_gallery && node.relationships.image_gallery.length) {
    images = [...images, ...node.relationships.image_gallery];
    imageMeta = [...imageMeta, ...node.image_gallery];
  }
  if (node.relationships?.thumb_image_gallery && node.relationships.thumb_image_gallery.length) {
    thumbs = [...thumbs, ...node.relationships.thumb_image_gallery];
  }

  const labelDeck = node.relationships.label_deck?.url;

  const bvID = node?.field_bazaarvoice_id
    ? `${node?.field_bazaarvoice_id.slice(0, 5)}-${node?.field_bazaarvoice_id.slice(5)}`
    : '';
  const showFeedingTab =
    node.feeding_instructions || (node.relationships?.servingSize && feedingCalculator);
  const availableSizes = skus?.length >= 1 ? getProductVariationsList(skus, 'product') : [];

  return (
    <Layout
      node={node}
      metaData={node.metatag}
      language={node.langcode}
      translations={translations}
    >
      <Seo {...helmetProps} schemaMarkup={schemaMarkup} />
      {bvID && <BazaarvoiceDCC token={node.jwt_token} />}
      <StyledProductContainer fluid className="p-0">
        <Container fluid="sm">
          <Row>
            <Col md={6} className="text-center mb-4 mb-md-0 bg-light px-0 py-2">
              <ProductCarousel
                productTitle={node.title}
                images={images}
                thumbs={thumbs}
                imageMeta={imageMeta}
              />
            </Col>
            <Col md={6} className="d-md-flex flex-md-column justify-content-md-center">
              <ProductTitle
                node={node}
                bvID={bvID}
                sku={sku}
                productStatus={productStatus}
                brand={brand}
                relatedProducts={relatedProducts}
                showPricespiderRetailers={node.field_show_pricespider_retailers ?? false}
              />
            </Col>
          </Row>
        </Container>
      </StyledProductContainer>
      <Container>
        <ProductTabs
          location={location}
          node={node}
          availableSizes={availableSizes}
          labelDeck={labelDeck}
          showFeedingTab={showFeedingTab}
          feedingCalculator={feedingCalculator}
          bvID={bvID}
        />
      </Container>
      {relatedProducts?.edges.length > 0 && <RelatedProducts relatedProducts={relatedProducts} />}
    </Layout>
  );
};

export const query = graphql`
  query ($id: String!, $language: String!, $nid: Int!, $brand_id: Int, $species: String) {
    relatedProducts: allNodeProduct(
      limit: 20
      sort: { created: DESC }
      filter: {
        field_brand: { drupal_internal__target_id: { eq: $brand_id } }
        id: { ne: $id }
        langcode: { eq: $language }
        relationships: { field_product_status: { field_exclude: { ne: true } } }
      }
    ) {
      edges {
        node {
          title
          path {
            alias
          }
          default_langcode
          langcode
          field_bazaarvoice_id
          field_alternate_url {
            uri
          }
          relationships {
            image: field_product_image {
              gatsbyImage(width: 400, height: 400, formats: AUTO, placeholder: NONE)
            }
            skus: field_product_variations {
              id
              upc: field_upc_code
            }
          }
        }
      }
    }
    feedingCalculator: storageFeedingCalculator(field_species_code: { eq: $species }) {
      ...StorageFeedingCalculator
    }
    translations: allNodeProduct(
      filter: { drupal_internal__nid: { eq: $nid }, langcode: { ne: $language } }
    ) {
      edges {
        node {
          langcode
          path {
            alias
          }
        }
      }
    }
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    node: nodeProduct(id: { eq: $id }, langcode: { eq: $language }) {
      internal {
        type
      }
      id
      title
      default_langcode
      langcode
      body {
        processed
        summary
      }
      feeding_instructions: field_feeding_instructions {
        processed
      }
      created
      field_bazaarvoice_id
      field_show_pricespider_retailers
      highlights: field_highlights
      path {
        alias
      }
      ...Metatag
      image: field_product_image {
        alt
        height
        width
      }
      image_gallery: field_product_image_gallery {
        alt
        height
        width
      }
      statusLink: field_product_status_link {
        url
        title
      }
      guaranteedAnalysis: field_guaranteed_analysis {
        processed
      }
      ingredientCode: field_ingredient_code
      jwt_token
      relationships {
        image: field_product_image {
          gatsbyImage(width: 1000, formats: AUTO, placeholder: NONE)
        }
        image_gallery: field_product_image_gallery {
          gatsbyImage(width: 1000, formats: AUTO, placeholder: NONE)
        }
        thumb_image: field_product_image {
          gatsbyImage(width: 100, height: 100, formats: AUTO, placeholder: NONE, layout: FIXED)
        }
        thumb_image_gallery: field_product_image_gallery {
          gatsbyImage(width: 100, height: 100, formats: AUTO, placeholder: NONE, layout: FIXED)
        }
        label_deck: field_label_deck_file {
          url
        }
        skus: field_product_variations {
          ...ParagraphProductVariation
        }
        brand: field_brand {
          name
          relationships {
            parent {
              name
              relationships {
                parent {
                  name
                  relationships {
                    field_page_delegate {
                      path {
                        alias
                      }
                    }
                  }
                }
                field_page_delegate {
                  path {
                    alias
                  }
                }
              }
            }
            field_page_delegate {
              path {
                alias
              }
            }
          }
        }
        category: field_category {
          name
          relationships {
            parent {
              name
              relationships {
                parent {
                  name
                }
              }
            }
          }
        }
        status: field_product_status {
          name
          field_exclude
          variant: field_alert_type
        }
        ingredients: field_ingredients {
          ...TaxonomyTermIngredients
        }
        servingSize: field_serving_sizes {
          ...ParagraphServingSize
        }
      }
    }
  }
`;

export default ProductTemplate;
